import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

const useFilterPreview = ({
  filters,
  filterData,
  isEn,
  dateFormat,
  router,
  sports,
}) => {
  const [sportPreviewVal, setSportPreviewVal] = useState([]);
  const [destPreviewVal, setDestPreviewVal] = useState([]);
  const [datePreviewVal, setDatePreviewVal] = useState([]);

  const { t } = useTranslation("common");

  // Helper function to get title based on alias and type
  const getTitleFromAlias = (alias, type, sports) => {
    switch (type) {
      case "sport":
        const sportMatch = sports.find((sp) => sp.alias === alias);
        if (sportMatch) {
          return sportMatch;
        }
        return filterData?.sports?.categories?.find((sp) => sp.alias === alias);
      case "sub_sport":
        return filterData?.sports?.categories
          ?.flatMap((sp) => sp.sub_categories)
          ?.find((subSp) => subSp.alias === alias);
      case "continent":
      case "country":
      case "state":
        return filterData?.destinations?.continents
          ?.flatMap((continent) => [
            continent,
            ...(continent.countries || []),
            ...(continent.countries?.flatMap((country) => country.states) ||
              []),
          ])
          ?.find((item) => item.alias === alias);
      case "month":
        return filterData?.months?.find((month) => month.alias === alias);
      case "all_sports":
        return {
          title: t("frontpage.all_sports_title"),
          alias: "all",
          id: 0,
          type: "all_sports",
        };
      case "all_destinations":
        return {
          title: t("frontpage.all_des"),
          alias: "all",
          id: 0,
          type: "all_destinations",
        };
      default:
        return null;
    }
  };

  // Process selected filters
  const selectedFilters = useMemo(() => {
    if (!filters || Object.keys(filters).length === 0) return [];
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        return [...acc, ...value.map((val) => ({ type: key, value: val }))];
      } else if (value === 1 || value === true) {
        return [...acc, { type: key, value: key }];
      } else if (value && typeof value === "string") {
        return [...acc, { type: key, value }];
      }
      return acc;
    }, []);
  }, [filters]);

  // Update preview values
  useEffect(() => {
    if (selectedFilters.length === 0) return;

    const finalSport = [];
    const finalDest = [];
    const finalDate = [];

    selectedFilters.forEach((filter) => {
      const { type, value } = filter;
      const item = getTitleFromAlias(value, type, sports);

      if (item) {
        const title =
          item?.type === "sport"
            ? item.title
            : isEn
            ? item.title_en
            : item.title_de;
        const previewItem = { title, alias: item.alias, id: item.id, type };

        if (type === "sport") {
          finalSport.push(previewItem);
        } else if (
          type === "continent" ||
          type === "country" ||
          type === "state"
        ) {
          finalDest.push(previewItem);
        }

        if (type === "all_sports" && value === "true") {
          finalSport.push(item);
        }
        if (type === "all_destinations" && value === "true") {
          finalDest.push(item);
        }
      } else if (
        type === "continent" ||
        type === "country" ||
        type === "state"
      ) {
        const storedDestFilter = JSON.parse(
          localStorage.getItem("destinationFilter")
        );
        if (storedDestFilter && storedDestFilter?.displayTitle) {
          const enTitle =
            storedDestFilter?.displayTitle?.en ??
            storedDestFilter?.displayTitle?.de;
          const deTitle =
            storedDestFilter?.displayTitle?.de ??
            storedDestFilter?.displayTitle?.en;

          finalDest.push({
            title: enTitle
              ? isEn
                ? enTitle
                : deTitle
              : storedDestFilter?.displayTitle,
            alias: storedDestFilter?.alias,
            id: storedDestFilter?.id,
            type: storedDestFilter?.type,
          });
        } else {
          finalDest.push({
            title: value
              ?.replace(/-/g, " ")
              ?.replace(/\b\w/g, (char) => char.toUpperCase()),
            alias: value,
            id: null,
            type,
          });
        }
      } else if (type === "sport") {
        finalSport.push({
          title: value
            ?.replace(/-/g, " ")
            ?.replace(/\b\w/g, (char) => char.toUpperCase()),
          alias: value,
          id: null,
          type,
        });
      } else if (type === "month") {
        finalDate.push({
          title: moment(value, "YYYY-MM")
            .locale(isEn ? "en" : "de")
            .format("MMMM YYYY"),
          alias: value,
          type: "months",
        });
      }
    });

    // Handle exact dates
    const fromDate = filters?.from;
    const toDate = filters?.to;
    if (
      fromDate &&
      toDate &&
      moment(fromDate, "YYYY-MM-DD").isValid() &&
      moment(toDate, "YYYY-MM-DD").isValid()
    ) {
      finalDate.push(
        {
          title: moment(fromDate, "YYYY-MM-DD").format(dateFormat),
          alias: fromDate,
          type: "from_date",
        },
        {
          title: moment(toDate, "YYYY-MM-DD").format(dateFormat),
          alias: toDate,
          type: "to_date",
        }
      );
    }

    setSportPreviewVal(finalSport);
    setDestPreviewVal(finalDest);
    setDatePreviewVal(finalDate);
  }, [selectedFilters, filterData, router.query, filters]);

  // clear filters
  const clearSports = () => {
    setSportPreviewVal([]);
  };

  const clearDests = () => {
    setDestPreviewVal([]);
  };

  const clearDates = () => {
    setDatePreviewVal([]);
  };

  return {
    sportPreviewVal,
    destPreviewVal,
    datePreviewVal,
    clearSports,
    clearDests,
    clearDates,
  };
};

export default useFilterPreview;
